import { z } from "zod";

/**
 * Wire-level Project shape returned by `GET /api/projects` and used by
 * the CLI to filter/render. `user_id` and `org_id` are intentionally
 * included; CLI commands key on them for client-side filtering. If a
 * future change strips these for security, expect this contract to
 * fail-fast at the boundary — that's correct behavior, not a bug.
 *
 * Field types mirror `webapp/lib/projects/types.ts` Project interface.
 * description and client_name are typed string | null in the webapp
 * Project type (the DB schema allows NULL). getProjects() coerces nulls
 * to "" at runtime, so the wire value is always a string, but TypeScript
 * sees string | null from the source type. The schema uses .nullable()
 * so respondWith()'s z.input<T> check accepts the webapp type as-is
 * without requiring a manual cast in the route.
 *
 * color, icon, created_at, updated_at are also coerced to non-null
 * defaults by getProjects(), but the DB-level type for those columns is
 * NOT NULL, so the TS type is already string — no .nullable() needed.
 */
export const Project = z.object({
  id: z.string(),
  user_id: z.string(),
  org_id: z.string(),
  name: z.string(),
  type: z.literal("content-creation"),
  description: z.string().nullable(),
  client_name: z.string().nullable(),
  color: z.string(),
  icon: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Project = z.infer<typeof Project>;

export const ProjectListResponse = z.object({
  projects: z.array(Project),
  total: z.number(),
});
export type ProjectListResponse = z.infer<typeof ProjectListResponse>;

/**
 * Wire-level response for POST /api/projects (project create).
 * The route returns { project } at HTTP 201, plus `cascade` when the
 * caller supplied a domain (see below) — omitted entirely otherwise.
 *
 * Reuses the Project schema — the webapp's createProject() returns the
 * same DB row shape that getProjects() does, so the same nullable rules
 * apply to description and client_name.
 */
export const ProjectCreateResponse = z.object({
  project: Project,
  /**
   * Present when the caller supplied a domain. `workflowId` is null
   * only when `startBrandExtraction` itself failed to start a cascade
   * — a plan-limit 403, an already-in-progress 409, an
   * already-extracted 400, or a brand_config/Temporal failure (500 /
   * 502). It is NOT tied to the `onboarding-cascade` feature flag:
   * the flag being off still starts extraction and returns a real
   * `workflowId` — the flag only governs whether the workflow
   * auto-advances past extraction into prompts/scan.
   */
  cascade: z.object({ workflowId: z.string().nullable() }).optional(),
});
export type ProjectCreateResponse = z.infer<typeof ProjectCreateResponse>;
